import { guardGameSurface } from './186-game-surface.mjs';
import { createDragSurface as createGameDragSurface } from './186-drag-surface.mjs';
import {
  PICTURE_PUZZLE_SIZES,
  createPicturePuzzle,
  picturePuzzleView,
  applyPictureMove,
  undoPicturePuzzle,
  nextPicturePuzzle,
} from '../../../packages/domain/src/61-picture-puzzles.mjs';
import { loadPrivatePicture, PRIVATE_PICTURE_ACCEPT } from './62-private-raster.mjs';
import { paintPictureBoard } from './63-picture-artwork.mjs';

const builtin = Object.freeze({
  url: new URL(
    '../assets/lifestyle-amusing-animals-050-a-window-fully-occupied.webp',
    import.meta.url,
  ).href,
});

/** Optional isolated private picture practice. Domain61 owns all transitions;
 * raster62 owns safe decode. No canonical game/session, photo upload, persistence,
 * telemetry, share or score API is used. The caller guards entry and halts its
 * prior presentation. Modal closure retains ephemeral artwork until disposal. */
export function createPictureStudio(
  ctx,
  {
    host,
    game,
    canOpen = () => true,
    onOpen = () => {},
    seed = 7,
    loadPicture = loadPrivatePicture,
  },
) {
  const button = ctx.e('button', 'game-action', 'Picture studio');
  button.type = 'button';
  button.hidden = game.id !== 'tile-slide';
  const dialog = ctx.e('dialog', 'picture-studio');
  dialog.setAttribute('aria-label', 'Picture studio · private unscored practice');
  const header = ctx.e('header', 'picture-heading');
  const title = ctx.e('h2', '', 'Picture studio');
  const close = ctx.e('button', 'picture-action', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close picture studio');
  header.append(title, close);
  const tools = ctx.e('details', 'picture-tools');
  const summary = ctx.e('summary', '', 'Picture tools');
  const toolOptions = ctx.e('div', 'picture-tool-options');
  const size = ctx.e('select', 'picture-size');
  size.setAttribute('aria-label', 'Puzzle size');
  for (const layout of PICTURE_PUZZLE_SIZES) {
    const option = ctx.e('option', '', layout.label);
    option.value = layout.id;
    size.append(option);
  }
  size.value = '3x3';
  const fit = ctx.e('select', 'picture-fit');
  fit.setAttribute('aria-label', 'Picture framing');
  for (const [value, label] of [
    ['contain', 'Full image'],
    ['cover', 'Fill frame'],
  ]) {
    const option = ctx.e('option', '', label);
    option.value = value;
    fit.append(option);
  }
  fit.value = 'contain';
  const dog = ctx.e('button', 'picture-action', 'Original dog');
  dog.type = 'button';
  toolOptions.append(size, fit, dog);
  tools.append(summary, toolOptions);
  const caption = ctx.e('p', 'picture-caption');
  const stage = ctx.e('div', 'picture-stage');
  const board = ctx.e('div', 'picture-board');
  board.setAttribute('aria-label', 'Sliding picture fragments');
  stage.append(board);
  const result = ctx.e('div', 'picture-result');
  result.setAttribute('role', 'status');
  const finished = ctx.e('strong', '', 'Picture complete');
  const next = ctx.e('button', 'picture-next', 'Next picture');
  next.type = 'button';
  result.append(finished, next);
  const controls = ctx.e('footer', 'picture-controls');
  const undo = ctx.e('button', 'picture-action', 'Undo');
  const fresh = ctx.e('button', 'picture-action', 'New');
  const photo = ctx.e('button', 'picture-action', 'Photo');
  for (const action of [undo, fresh, photo]) action.type = 'button';
  photo.setAttribute('aria-label', 'Choose a private local photo');
  const file = ctx.e('input');
  file.type = 'file';
  file.accept = PRIVATE_PICTURE_ACCEPT;
  file.hidden = true;
  controls.append(undo, fresh, photo, file);
  const status = ctx.e('p', 'picture-status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  dialog.append(header, caption, tools, stage, result, controls, status);
  let state = createPicturePuzzle('3x3', seed),
    picture = builtin,
    handle = null;
  let active = false,
    disposed = false,
    loading = false,
    resolving = false,
    generation = 0,
    gesture = null,
    suppressClick = false,
    observer = null;
  const animations = new Set(),
    ghosts = new Set();
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const find = (index) => [...board.children].find((node) => node.dataset.cell === String(index));
  function render() {
    const view = paintPictureBoard(ctx, board, state, picture, move, {
      disabled: loading || resolving,
      fit: fit.value,
    });
    caption.textContent =
      picture === builtin
        ? 'Original dog · private unscored practice'
        : 'Your photo · stays on this device · unscored';
    summary.textContent = `Picture tools · ${state.columns}×${state.rows}`;
    result.hidden = !view.completed;
    result.setAttribute('aria-hidden', String(!view.completed));
    next.disabled = !view.completed || loading || resolving;
    undo.disabled = !view.canUndo || loading || resolving;
    fresh.disabled =
      photo.disabled =
      size.disabled =
      dog.disabled =
      fit.disabled =
        loading || resolving;
    status.textContent = loading
      ? 'Preparing your photo locally…'
      : `${view.progress.current}/${view.progress.total} fragments · ${view.moves} ${view.moves === 1 ? 'move' : 'moves'}`;
  }
  function releaseGesture() {
    const old = gesture;
    gesture = null;
    if (old?.piece) old.piece.style.visibility = old.visibility;
    old?.ghost?.remove();
    if (old?.ghost) ghosts.delete(old.ghost);
    if (old && board.hasPointerCapture?.(old.id)) board.releasePointerCapture?.(old.id);
  }
  function cancel() {
    generation++;
    releaseGesture();
    for (const animation of animations) animation.cancel();
    for (const ghost of ghosts) ghost.remove();
    animations.clear();
    ghosts.clear();
    resolving = false;
    loading = false;
    if (active && !disposed) render();
  }
  function focusBoard() {
    ([...board.children].find((node) => !node.disabled) || summary).focus?.({
      preventScroll: true,
    });
  }
  function tileIndex(event) {
    for (const node of board.children) {
      const rect = node.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX < rect.right &&
        event.clientY >= rect.top &&
        event.clientY < rect.bottom
      )
        return Number(node.dataset.cell);
    }
    return -1;
  }
  function ghostFor(index) {
    const piece = find(index),
      rect = piece.getBoundingClientRect(),
      origin = board.getBoundingClientRect();
    const ghost = ctx.e('span', 'picture-flight');
    ghost.setAttribute('aria-hidden', 'true');
    Object.assign(ghost.style, {
      left: `${rect.left - origin.left}px`,
      top: `${rect.top - origin.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
    });
    ghost.append(piece.firstChild.cloneNode(true));
    board.append(ghost);
    ghosts.add(ghost);
    return { ghost, rect };
  }
  function refresh() {
    if (active && (!canOpen() || host.isConnected === false)) dialog.close();
    return active && !disposed && canOpen();
  }
  function ready() {
    return refresh() && !loading && !resolving;
  }
  function optionalAnimation(node, frames, timing) {
    try {
      return node.animate(frames, timing);
    } catch {
      return null;
    }
  }
  function move(index, flight = null) {
    if (!ready()) return;
    const view = picturePuzzleView(state),
      cell = view.cells[index];
    if (!cell || cell.disabled || view.completed) {
      flight?.ghost.remove();
      if (flight?.ghost) ghosts.delete(flight.ghost);
      return;
    }
    const previous = state,
      destination = state.board.indexOf(0),
      targetRect = find(destination).getBoundingClientRect();
    const ownsFocus = document.activeElement === find(index);
    const supplied = flight || (!media.matches && !document.hidden ? ghostFor(index) : null);
    try {
      state = applyPictureMove(state, index);
    } catch {
      supplied?.ghost.remove();
      if (supplied?.ghost) ghosts.delete(supplied.ghost);
      return;
    }
    const token = generation;
    resolving = Boolean(
      supplied &&
      !media.matches &&
      !document.hidden &&
      typeof supplied.ghost.animate === 'function',
    );
    render();
    if (!resolving) {
      supplied?.ghost.remove();
      if (supplied?.ghost) ghosts.delete(supplied.ghost);
      if (ownsFocus) find(destination)?.focus?.({ preventScroll: true });
      return;
    }
    const { ghost, rect } = supplied;
    // Reconciliation removes presentation ghosts; reattach exactly one above
    // accepted native state, leaving the moving destination hidden until landing.
    board.append(ghost);
    const target = find(destination).firstChild;
    target.style.visibility = 'hidden';
    const start = ghost.style.transform || 'translate(0px,0px)';
    const animation = optionalAnimation(
      ghost,
      [
        { transform: start },
        { transform: `translate(${targetRect.left - rect.left}px,${targetRect.top - rect.top}px)` },
      ],
      { duration: 240, easing: 'cubic-bezier(.2,.7,.25,1)', fill: 'forwards' },
    );
    if (!animation) {
      ghost.remove();
      ghosts.delete(ghost);
      resolving = false;
      render();
      if (ownsFocus) find(destination)?.focus?.({ preventScroll: true });
      return;
    }
    animations.add(animation);
    const settle = () => {
      animation.cancel();
      animations.delete(animation);
      ghost.remove();
      ghosts.delete(ghost);
      if (token !== generation || disposed) return;
      resolving = false;
      render();
      if (ownsFocus) find(destination)?.focus?.({ preventScroll: true });
    };
    void animation.finished.then(settle, settle);
    if (state === previous) cancel();
  }
  function down(event) {
    if (event.button !== 0 || event.isPrimary === false || gesture || !ready()) return;
    const index = tileIndex(event),
      view = picturePuzzleView(state);
    if (!view.cells[index] || view.cells[index].disabled) return;
    gesture = {
      id: event.pointerId,
      index,
      x: event.clientX,
      y: event.clientY,
      state,
      moved: false,
    };
    suppressClick = false;
    board.setPointerCapture?.(event.pointerId);
  }
  const dragSurface = createGameDragSurface(board);
  function drag(event) {
    if (!gesture || event.pointerId !== gesture.id) return;
    if (gesture.state !== state || !ready()) return releaseGesture();
    const dx = event.clientX - gesture.x,
      dy = event.clientY - gesture.y;
    if (!gesture.moved && Math.hypot(dx, dy) < 8) return;
    if (!gesture.moved) {
      Object.assign(gesture, ghostFor(gesture.index));
      gesture.piece = find(gesture.index);
      gesture.visibility = gesture.piece.style.visibility || '';
      gesture.piece.style.visibility = 'hidden';
      gesture.moved = true;
    }
    dragSurface.carry(gesture.ghost, `translate(${dx}px,${dy}px)`);
    event.preventDefault();
  }
  function up(event) {
    if (!gesture || gesture.id !== event.pointerId) return;
    drag(event);
    if (!gesture) return;
    dragSurface.flush();
    const old = gesture;
    const valid = old.state === state && ready();
    const destination = tileIndex(event);
    if (old.piece) old.piece.style.visibility = old.visibility;
    gesture = null;
    if (board.hasPointerCapture?.(old.id)) board.releasePointerCapture?.(old.id);
    suppressClick = true;
    if (!valid) {
      old.ghost?.remove();
      if (old.ghost) ghosts.delete(old.ghost);
      return;
    }
    const stationary = Math.hypot(event.clientX - old.x, event.clientY - old.y) < 8;
    if (valid && ((!old.moved && stationary) || destination === state.board.indexOf(0)))
      move(old.index, old.moved ? old : null);
    else if (old.ghost) {
      const token = generation;
      if (!media.matches && typeof old.ghost.animate === 'function') {
        const animation = optionalAnimation(
          old.ghost,
          [{ transform: old.ghost.style.transform }, { transform: 'translate(0px,0px)' }],
          { duration: 180, easing: 'ease-out' },
        );
        if (!animation) {
          old.ghost.remove();
          ghosts.delete(old.ghost);
          return;
        }
        animations.add(animation);
        if (old.piece) old.piece.style.visibility = 'hidden';
        const restore = () => {
          if (old.piece) old.piece.style.visibility = old.visibility;
        };
        void animation.finished.then(
          () => {
            restore();
            old.ghost.remove();
            ghosts.delete(old.ghost);
            animations.delete(animation);
          },
          () => {
            restore();
            if (token === generation) old.ghost.remove();
          },
        );
      } else {
        old.ghost.remove();
        ghosts.delete(old.ghost);
      }
    }
  }
  const unguard = guardGameSurface(board, {
    cancel: () => {
      dragSurface.reset();
      cancel();
    },
  });
  board.addEventListener('pointerdown', down);
  board.addEventListener('pointermove', drag);
  board.addEventListener('pointerup', up);
  board.addEventListener('pointercancel', (event) => {
    if (gesture?.id === event.pointerId) releaseGesture();
  });
  board.addEventListener('lostpointercapture', (event) => {
    if (gesture?.id === event.pointerId) releaseGesture();
  });
  board.addEventListener(
    'click',
    (event) => {
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true,
  );
  function newBoard() {
    if (!ready()) return;
    cancel();
    state = createPicturePuzzle(size.value, (state.seed + 1) >>> 0);
    render();
    focusBoard();
  }
  undo.addEventListener('click', () => {
    if (!ready()) return;
    cancel();
    state = undoPicturePuzzle(state);
    render();
    focusBoard();
  });
  fresh.addEventListener('click', newBoard);
  size.addEventListener('change', () => {
    if (!ready()) return;
    tools.open = false;
    newBoard();
  });
  fit.addEventListener('change', () => {
    if (!ready()) return;
    cancel();
    render();
  });
  next.addEventListener('click', () => {
    if (!ready() || !state.completed) return;
    cancel();
    state = nextPicturePuzzle(state);
    render();
    focusBoard();
  });
  dog.addEventListener('click', () => {
    if (!ready()) return;
    cancel();
    handle?.dispose();
    handle = null;
    picture = builtin;
    newBoard();
  });
  photo.addEventListener('click', () => {
    if (!ready()) return;
    file.value = '';
    file.click();
  });
  file.addEventListener('change', async () => {
    const chosen = file.files?.[0];
    file.value = '';
    if (!chosen || !ready()) return;
    cancel();
    const token = generation;
    loading = true;
    render();
    try {
      const candidate = await loadPicture(chosen, {
        isCurrent: () => active && !disposed && canOpen() && token === generation,
      });
      if (!refresh() || token !== generation) {
        candidate.dispose();
        return;
      }
      const prior = handle;
      handle = candidate;
      picture = candidate;
      state = createPicturePuzzle(size.value, state.seed);
      prior?.dispose();
      loading = false;
      render();
      focusBoard();
    } catch (error) {
      if (refresh() && token === generation) {
        loading = false;
        render();
        status.textContent = error.message;
      }
    }
  });
  function restore() {
    if (!active) return;
    active = false;
    observer?.disconnect();
    observer = null;
    loading = false;
    cancel();
    const more = host.querySelector?.('.game-more');
    (more?.querySelector('summary') || button).focus?.({ preventScroll: true });
  }
  function open() {
    if (
      disposed ||
      active ||
      game.id !== 'tile-slide' ||
      !canOpen() ||
      host.isConnected === false ||
      typeof dialog.showModal !== 'function'
    )
      return;
    onOpen();
    const more = host.querySelector?.('.game-more');
    if (more) more.open = false;
    document.body.append(dialog);
    active = true;
    render();
    try {
      dialog.showModal();
      if (typeof MutationObserver === 'function') {
        observer = new MutationObserver(() => {
          if (host.isConnected === false) dispose();
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
      close.focus?.({ preventScroll: true });
    } catch {
      restore();
    }
  }
  button.addEventListener('click', open);
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('close', restore);
  const hidden = () => {
    if (document.hidden) cancel();
  };
  const reduced = () => {
    if (media.matches) cancel();
  };
  document.addEventListener('visibilitychange', hidden);
  media.addEventListener?.('change', reduced);
  function dispose() {
    unguard();
    dragSurface.reset();
    if (disposed) return;
    disposed = true;
    generation++;
    observer?.disconnect();
    observer = null;
    if (active) dialog.close();
    cancel();
    handle?.dispose();
    handle = null;
    picture = builtin;
    button.removeEventListener('click', open);
    document.removeEventListener('visibilitychange', hidden);
    media.removeEventListener?.('change', reduced);
    dialog.remove();
  }
  return {
    button,
    get active() {
      return active;
    },
    cancel,
    refresh,
    dispose,
  };
}
